// apps/client/src/pages/leads/edm/EdmImportPage.tsx
// /leads/edm/import: bring a contact list from another email platform.
// Four steps: upload, mapping, file role + consent, result. Company-wide
// roles only (RequireEdmImportAccess). The server is the authority for every
// check; nothing here is shown or downloadable beyond the 20 sample rows.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle, FileUp, Info } from 'lucide-react';
import { PageHeader } from '../../../components/common/PageHeader';
import { Spinner } from '../../../components/common/Spinner';
import CountStrip from './components/CountStrip';
import { useLeadCaptureOnboarding } from '../../../hooks/useLeadCaptureOnboarding';
import { useEdmImport } from '../../../hooks/useEdmImport';
import type { EdmImportExt, EdmImportPreview, EdmImportResult, EdmMapping } from '../../../hooks/useEdmImport';
import {
  EDM_UI,
  edmErrorMessage,
  renderImportAttestation,
  suggestRoleFromFilename,
} from '../../../utils/edm';
import type { EdmFileRole } from '../../../utils/edm';
import ImportDropzone from './components/ImportDropzone';
import MappingTable, { mappingError } from './components/MappingTable';

interface OutletCtx {
  userData?: { companyId?: string; role?: string };
}

type Step = 1 | 2 | 3 | 4;

interface PendingImport {
  importId: string;
  mapping: EdmMapping;
  fileRole: EdmFileRole;
  attestationText?: string;
}

const STEPS: Array<{ n: Step; label: string }> = [
  { n: 1, label: 'Upload' },
  { n: 2, label: 'Mapping' },
  { n: 3, label: 'File role' },
  { n: 4, label: 'Result' },
];

const ROLES: Array<{ value: EdmFileRole; label: string; description: string }> = [
  { value: 'contacts', label: 'Contacts I can email', description: 'People who agreed to receive marketing email from your business.' },
  { value: 'unsubscribed', label: 'Unsubscribed list', description: 'Everyone in this file is added to your suppression list and never emailed.' },
  { value: 'bounced', label: 'Bounced / cleaned list', description: 'Everyone in this file is added to your suppression list and never emailed.' },
];

const storageKey = (companyId: string) => `edmImport:${companyId}`;

function readPending(companyId: string | undefined): PendingImport | null {
  if (!companyId) return null;
  try {
    const raw = sessionStorage.getItem(storageKey(companyId));
    return raw ? (JSON.parse(raw) as PendingImport) : null;
  } catch {
    return null;
  }
}

function writePending(companyId: string, p: PendingImport | null): void {
  try {
    if (p) sessionStorage.setItem(storageKey(companyId), JSON.stringify(p));
    else sessionStorage.removeItem(storageKey(companyId));
  } catch {
    // Storage unavailable (private window): resume-after-leave is lost, the import itself is not.
  }
}

/** Errors that do not end the import: the commit may still be running server-side. */
const RETRYABLE = new Set(['deadline-exceeded', 'unavailable']);
const isRetryable = (err: unknown): boolean => {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === 'string' && RETRYABLE.has(code.replace(/^functions\//, ''));
};

const EdmImportPage: React.FC = () => {
  const ctx = useOutletContext<OutletCtx>();
  const companyId = ctx?.userData?.companyId;
  const api = useEdmImport(companyId);
  const { data: onboarding, loading: onboardingLoading, error: onboardingError } = useLeadCaptureOnboarding(companyId);
  // The attestation names the company, so it is shown (and can be ticked)
  // only once branding has loaded; otherwise a user could attest to the
  // "your business" placeholder and then send the real name.
  const onboardingReady = !onboardingLoading && onboarding !== null;
  const attestationText = renderImportAttestation(onboarding?.branding?.companyName);

  const [step, setStep] = useState<Step>(1);
  const [fileName, setFileName] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [reading, setReading] = useState(false);
  const [importId, setImportId] = useState<string | null>(null);
  const [preview, setPreview] = useState<EdmImportPreview | null>(null);
  const [mapping, setMapping] = useState<EdmMapping>({});
  const [fileRole, setFileRole] = useState<EdmFileRole>('contacts');
  const [attested, setAttested] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<EdmImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastParams, setLastParams] = useState<PendingImport | null>(null);
  const resumed = useRef(false);

  // A tick applies to the exact text shown; any change to it clears the tick.
  useEffect(() => {
    setAttested(false);
  }, [attestationText]);

  const reset = () => {
    setStep(1);
    setFileName(null);
    setProgress(null);
    setImportId(null);
    setPreview(null);
    setMapping({});
    setFileRole('contacts');
    setAttested(false);
    setResult(null);
    setError(null);
    setLastParams(null);
  };

  const confirm = useCallback(
    async (params: PendingImport) => {
      if (!companyId) return;
      setLastParams(params);
      writePending(companyId, params);
      setImporting(true);
      setError(null);
      try {
        const res = await api.confirmMapping(params);
        writePending(companyId, null);
        setResult(res);
        setStep(4);
      } catch (err) {
        if (!isRetryable(err)) writePending(companyId, null);
        setError(edmErrorMessage(err));
      } finally {
        setImporting(false);
      }
    },
    [api, companyId],
  );

  // A tab closed mid-commit: re-call confirmMapping with the identical params.
  // The server returns the stored counters once the import has completed.
  useEffect(() => {
    if (resumed.current || !companyId) return;
    resumed.current = true;
    const pending = readPending(companyId);
    if (pending) {
      setImportId(pending.importId);
      setMapping(pending.mapping);
      setFileRole(pending.fileRole);
      setStep(3);
      void confirm(pending);
    }
  }, [companyId, confirm]);

  const onFile = async (file: File, ext: EdmImportExt) => {
    setError(null);
    setFileName(file.name);
    setFileRole(suggestRoleFromFilename(file.name));
    try {
      // The storage rule admits the upload only once the import doc exists.
      const { importId: id } = await api.beginImport(ext);
      setImportId(id);
      setProgress(0);
      await api.upload(id, ext, file, setProgress);
      setProgress(null);
      setReading(true);
      const p = await api.previewImport(id);
      setPreview(p);
      setMapping({ ...p.suggestedMapping });
      if (p.requiresNegativeRole && suggestRoleFromFilename(file.name) === 'contacts') setFileRole('unsubscribed');
      setStep(2);
    } catch (err) {
      setProgress(null);
      setError(edmErrorMessage(err));
    } finally {
      setReading(false);
    }
  };

  const mapErr = preview ? mappingError(mapping) : null;
  const contactsBlocked = preview?.requiresNegativeRole === true;
  const canImport =
    !importing &&
    importId !== null &&
    (fileRole !== 'contacts' || (onboardingReady && attested && !contactsBlocked));

  const startImport = () => {
    if (!importId) return;
    void confirm({
      importId,
      mapping,
      fileRole,
      ...(fileRole === 'contacts' ? { attestationText } : {}),
    });
  };

  const counters = result?.counters;
  const sum = counters
    ? counters.imported + counters.suppressed + counters.duplicate + counters.duplicateLead + counters.invalid + counters.excluded
    : 0;

  return (
    <div className="space-y-6">
      <Link to="/leads/edm" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 hover:text-gray-200">
        <ArrowLeft className="h-4 w-4" /> Back to campaigns
      </Link>
      <PageHeader
        icon={FileUp}
        title="Import contacts"
        subtitle="Bring a contact list from another email platform so you can email it from VALUTTEN"
      />

      <ol className="flex flex-wrap items-center gap-4">
        {STEPS.map((s) => {
          const done = step > s.n;
          const active = step === s.n;
          return (
            <li key={s.n} className="flex items-center gap-2 text-sm">
              <span
                className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                  done ? 'bg-emerald-500/20 text-emerald-300' : active ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                }`}
              >
                {done ? <CheckCircle className="h-4 w-4" /> : s.n}
              </span>
              <span className={active ? 'text-gray-100' : 'text-gray-400'}>{s.label}</span>
            </li>
          );
        })}
      </ol>

      <div className={`${EDM_UI.infoBanner} flex gap-2`}>
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <span>
          Contacts imported here are only used for eDM. The Setup Wizard&apos;s contact import creates leads instead, and
          those are not emailed by eDM. Suppressions from either import are always honoured.
        </span>
      </div>

      {error && (
        <div className={`${EDM_UI.errorBanner} flex flex-wrap items-center gap-3`} role="alert">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          {lastParams && step === 3 && !importing && (
            <button type="button" className={EDM_UI.secondaryButton} onClick={() => void confirm(lastParams)} disabled={importing}>
              Try again
            </button>
          )}
        </div>
      )}

      {step === 1 && (
        <div className={EDM_UI.card}>
          <h2 className={EDM_UI.heading}>
            <FileUp className={EDM_UI.headingIcon} /> Upload
          </h2>
          <ImportDropzone disabled={reading || progress !== null} progress={progress} fileName={fileName} onFile={(f, e) => void onFile(f, e)} />
          {reading && <Spinner size="sm" label="Reading your file…" />}
          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <div className="rounded-md border border-gray-700 bg-gray-800/40 p-3">
              <h4 className="mb-1 font-semibold text-gray-100">Mailchimp</h4>
              <p className="text-xs text-gray-400">
                Audience › All contacts › Export. Upload the subscribed, unsubscribed and cleaned files separately.
              </p>
            </div>
            <div className="rounded-md border border-gray-700 bg-gray-800/40 p-3">
              <h4 className="mb-1 font-semibold text-gray-100">Other platforms</h4>
              <p className="text-xs text-gray-400">
                Export contacts as CSV or Excel. A PDF works if it is a table with an email column.
              </p>
            </div>
          </div>
        </div>
      )}

      {step === 2 && preview && (
        <div className={EDM_UI.card}>
          <h2 className={EDM_UI.heading}>Mapping</h2>
          {preview.requiresNegativeRole && (
            <div className={`${EDM_UI.warnBanner} flex gap-2`}>
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>
                We could only read email addresses from this PDF, so it can only be imported as an unsubscribe or bounce
                list.
              </span>
            </div>
          )}
          <MappingTable
            columns={preview.columns}
            sampleRows={preview.sampleRows}
            mapping={mapping}
            suggested={preview.suggestedMapping}
            onChange={setMapping}
          />
          {(preview.brokerUnmatched ?? 0) > 0 && (
            <p className="text-sm text-amber-300">
              {(preview.brokerUnmatched ?? 0).toLocaleString('en-AU')} broker values match no active team member; those
              contacts are imported without a broker.
            </p>
          )}
          {mapErr && <p className="text-sm text-red-400">{mapErr}</p>}
          <div className="flex justify-end">
            <button type="button" className={EDM_UI.primaryButton} disabled={mapErr !== null} onClick={() => setStep(3)}>
              Continue
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className={EDM_UI.card}>
          <h2 className={EDM_UI.heading}>File role</h2>
          <div className="flex flex-col gap-3 sm:flex-row" role="radiogroup" aria-label="File role">
            {ROLES.map((r) => {
              const disabled = importing || (r.value === 'contacts' && contactsBlocked);
              return (
                <button
                  key={r.value}
                  type="button"
                  role="radio"
                  aria-checked={fileRole === r.value}
                  disabled={disabled}
                  onClick={() => setFileRole(r.value)}
                  className={`${fileRole === r.value ? EDM_UI.segmentActive : EDM_UI.segmentIdle} text-left disabled:opacity-50`}
                >
                  <div>{r.label}</div>
                  <div className="mt-1 text-xs font-normal text-gray-400">
                    {r.value === 'contacts' && contactsBlocked
                      ? 'Not available: this PDF can only be an unsubscribe or bounce list.'
                      : r.description}
                  </div>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gray-400">Unsubscribe, bounce and complaint columns are always honoured, whatever you pick.</p>
          {fileRole === 'contacts' && !onboardingReady && (
            onboardingError ? (
              <div className={EDM_UI.errorBanner}>Your company details could not be loaded. Refresh the page and try again.</div>
            ) : (
              <Spinner size="sm" label="Loading your company details…" />
            )
          )}
          {fileRole === 'contacts' && onboardingReady && (
            <label className="flex items-start gap-3 text-sm text-gray-200">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 flex-shrink-0"
                checked={attested}
                disabled={importing}
                onChange={(e) => setAttested(e.target.checked)}
              />
              <span>{attestationText}</span>
            </label>
          )}
          {importing ? (
            <Spinner
              size="sm"
              label="Importing… this can take up to 9 minutes. You can leave this page; come back to see the result."
            />
          ) : (
            <div className="flex justify-end">
              <button type="button" className={EDM_UI.primaryButton} disabled={!canImport} onClick={startImport}>
                Import {(preview?.totalRows ?? 0).toLocaleString('en-AU')} rows
              </button>
            </div>
          )}
        </div>
      )}

      {step === 4 && result && counters && (
        <div className={EDM_UI.card}>
          <h2 className={EDM_UI.heading}>Result</h2>
          {result.status === 'failed' && (
            <div className={`${EDM_UI.errorBanner} flex flex-wrap items-center gap-3`}>
              <span className="flex-1">The import did not complete. Please try again.</span>
              {lastParams && (
                <button type="button" className={EDM_UI.secondaryButton} disabled={importing} onClick={() => void confirm(lastParams)}>
                  Try again
                </button>
              )}
            </div>
          )}
          <CountStrip
            label="Import counts"
            items={[
              { label: 'Imported', value: counters.imported },
              { label: 'Suppressed', value: counters.suppressed },
              {
                label: 'Duplicates',
                value: counters.duplicate + counters.duplicateLead,
                hint: counters.duplicateLead > 0 ? `${counters.duplicateLead.toLocaleString('en-AU')} already leads` : undefined,
              },
              { label: 'Invalid email', value: counters.invalid },
              { label: 'Excluded (not subscribed)', value: counters.excluded },
            ]}
          />
          <p className="text-sm text-gray-300">
            {counters.total.toLocaleString('en-AU')} rows = {counters.imported.toLocaleString('en-AU')} +{' '}
            {counters.suppressed.toLocaleString('en-AU')} + {counters.duplicate.toLocaleString('en-AU')} +{' '}
            {counters.duplicateLead.toLocaleString('en-AU')} already leads + {counters.invalid.toLocaleString('en-AU')} +{' '}
            {counters.excluded.toLocaleString('en-AU')}
          </p>
          {sum !== counters.total && (
            <div className={EDM_UI.errorBanner}>
              Counts do not add up; contact VALUTTEN support with import {lastParams?.importId ?? importId}
            </div>
          )}
          <p className="text-sm text-gray-300">
            Imported contacts can be added to a campaign&apos;s audience under Imported contacts. Your uploaded file has been
            deleted.
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={EDM_UI.secondaryButton} onClick={reset}>
              Import another file
            </button>
            <Link to="/leads/edm" className={EDM_UI.primaryButton}>
              Back to campaigns
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default EdmImportPage;
